import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/App";
import { format, isBefore, startOfToday, addDays } from "date-fns";
import { getSlotsForDate } from "@/lib/availability";
import { cn } from "@/lib/utils";
import Footer from "@/components/Footer";
import {
  Calendar as CalendarIcon,
  Clock,
  User,
  Mail,
  Phone,
  Stethoscope,
  FileText,
  CheckCircle,
  AlertCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  RefreshCw
} from "lucide-react";

const LOGO_URL = "/logo.svg";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200"
};

const statusIcons = {
  pending: <AlertCircle className="h-4 w-4" />,
  confirmed: <CheckCircle className="h-4 w-4" />,
  completed: <CheckCircle className="h-4 w-4" />,
  cancelled: <XCircle className="h-4 w-4" />
};

const PatientDashboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [appointment, setAppointment] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [newDate, setNewDate] = useState(null);
  const [newTime, setNewTime] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing access link");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const [appointmentRes, settingsRes] = await Promise.all([
          apiClient.get(`/patient/appointment?token=${token}`),
          apiClient.get("/patient/settings")
        ]);
        setAppointment(appointmentRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load appointment");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const availableRescheduleSlots = getSlotsForDate(settings, newDate);

  useEffect(() => {
    if (newTime && !availableRescheduleSlots.includes(newTime)) {
      setNewTime("");
    }
  }, [availableRescheduleSlots, newTime]);

  const handleReschedule = async () => {
    if (!newDate || !newTime) {
      toast.error("Please select both date and time");
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiClient.post(
        `/patient/appointment/reschedule?token=${token}`,
        {
          preferred_date: format(newDate, "yyyy-MM-dd"),
          preferred_time: newTime
        }
      );
      
      setAppointment(response.data.appointment);
      setRescheduling(false);
      setNewDate(null);
      setNewTime("");
      toast.success("Appointment rescheduled successfully! A confirmation email has been sent.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reschedule appointment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your appointment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-xl border-0">
            <CardContent className="pt-8 pb-8 text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Access Error</h2>
              <p className="text-gray-600">{error}</p>
              <Button
                onClick={() => navigate("/")}
                className="bg-teal-600 hover:bg-teal-700"
              >
                Book New Appointment
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50 flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img
            src={LOGO_URL}
            alt="Jubail Specialist Hospital"
            className="h-12 w-auto"
            data-testid="patient-logo"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/book")}
            className="text-gray-600 hover:text-teal-600"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Book New
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="patient-title">
            Your Appointment
          </h1>
          <p className="text-gray-600">
            Reference: {appointment.id.substring(0, 8).toUpperCase()}
          </p>
        </div>

        <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm mb-6">
          <CardHeader className="border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">Appointment Details</CardTitle>
                <CardDescription>Your scheduled visit information</CardDescription>
              </div>
              <Badge
                variant="outline"
                className={`${statusColors[appointment.status]} flex items-center gap-1`}
              >
                {statusIcons[appointment.status]}
                {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-teal-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Patient Name</p>
                    <p className="font-medium text-gray-900">{appointment.patient_name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-teal-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">{appointment.patient_email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-teal-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium text-gray-900">{appointment.patient_phone}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Stethoscope className="h-5 w-5 text-teal-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Appointment Type</p>
                    <p className="font-medium text-gray-900">{appointment.appointment_type}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CalendarIcon className="h-5 w-5 text-teal-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="font-medium text-gray-900">{appointment.preferred_date}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-teal-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Time</p>
                    <p className="font-medium text-gray-900">{appointment.preferred_time}</p>
                  </div>
                </div>
              </div>
            </div>

            {appointment.notes && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-teal-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Notes</p>
                    <p className="font-medium text-gray-900">{appointment.notes}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reschedule Section */}
        {appointment.can_reschedule ? (
          <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-teal-600" />
                Reschedule Appointment
              </CardTitle>
              <CardDescription>
                You can reschedule your appointment up to 24 hours before the scheduled time
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!rescheduling ? (
                <Button
                  onClick={() => setRescheduling(true)}
                  variant="outline"
                  className="border-teal-200 text-teal-600 hover:bg-teal-50"
                  data-testid="start-reschedule-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Change Date/Time
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4 text-teal-600" />
                        New Date
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !newDate && "text-gray-500"
                            )}
                            data-testid="reschedule-date-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newDate ? format(newDate, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={newDate}
                            onSelect={setNewDate}
                            disabled={(date) => isBefore(date, addDays(startOfToday(), 1))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-teal-600" />
                        New Time
                      </Label>
                      <Select
                        value={newTime}
                        onValueChange={setNewTime}
                        disabled={!newDate || availableRescheduleSlots.length === 0}
                      >
                        <SelectTrigger data-testid="reschedule-time-select">
                          <SelectValue placeholder={newDate ? "Select time" : "Pick a date first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableRescheduleSlots.map((slot) => (
                            <SelectItem key={slot} value={slot}>
                              {slot}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {newDate && availableRescheduleSlots.length === 0 && (
                        <p className="text-sm text-amber-600">
                          No appointment slots are available for that day.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleReschedule}
                      className="bg-teal-600 hover:bg-teal-700"
                      disabled={submitting || !newDate || !newTime}
                      data-testid="confirm-reschedule-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Rescheduling...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Confirm Reschedule
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setRescheduling(false);
                        setNewDate(null);
                        setNewTime("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-lg border-0 bg-amber-50/80">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Rescheduling Not Available</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {appointment.status === 'completed' || appointment.status === 'cancelled'
                      ? `This appointment has been ${appointment.status}.`
                      : "Appointments cannot be rescheduled less than 24 hours before the scheduled time. Please call +233 30 340 1167 for assistance."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p className="mb-2">
            Need assistance? Contact us at{" "}
            <a href="tel:+233303401167" className="text-teal-600 hover:underline">
              +233 30 340 1167
            </a>
          </p>
          <p>Pentecost - Jubail Street, Sakumono, Greater Accra, Ghana</p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PatientDashboard;
